import type { PackItem, PackItemInput } from '../api/business/types';

/** The API's limit on pieces of one variant in a pack (R0030 WP-09). */
export const PACK_ITEM_MAX_QUANTITY = 9999;

/** What one item of a pack takes when the pack is sold. */
export type PackIssueLine = { name: string; count: number };

/** A pack's items as a write sends them back, in their order. */
export const packInputOf = (items: Array<PackItem>): Array<PackItemInput> =>
  items.map((item) => ({ variantId: item.variantId, quantity: item.quantity }));

/** `items` with `variantId` added at the end — or, already in the pack, its quantity raised by `quantity`. */
export const withPackItem = (
  items: Array<PackItemInput>,
  variantId: string,
  quantity: number,
): Array<PackItemInput> =>
  items.some((item) => item.variantId === variantId)
    ? items.map((item) =>
        item.variantId === variantId
          ? { ...item, quantity: Math.min(PACK_ITEM_MAX_QUANTITY, item.quantity + quantity) }
          : item,
      )
    : [...items, { variantId, quantity }];

/** `items` with the quantity of `variantId` set to `quantity`. */
export const withPackQuantity = (
  items: Array<PackItemInput>,
  variantId: string,
  quantity: number,
): Array<PackItemInput> => items.map((item) => (item.variantId === variantId ? { ...item, quantity } : item));

/** `items` with the one at `index` moved by `step` (−1 up, 1 down); unchanged at either end. */
export const movedPackItem = (
  items: Array<PackItemInput>,
  index: number,
  step: -1 | 1,
): Array<PackItemInput> => {
  const target = index + step;

  if (index < 0 || index >= items.length || target < 0 || target >= items.length) {
    return items;
  }

  const moved = [...items];

  [moved[index], moved[target]] = [moved[target], moved[index]];

  return moved;
};

/** `items` without `variantId`. */
export const withoutPackItem = (items: Array<PackItemInput>, variantId: string): Array<PackItemInput> =>
  items.filter((item) => item.variantId !== variantId);

/** A quantity typed for an item: a whole number from 1 to the limit, else undefined. */
export const parsePackQuantity = (text: string): number | undefined => {
  const value = Number(text.trim());

  return /^\d+$/.test(text.trim()) && value >= 1 && value <= PACK_ITEM_MAX_QUANTITY ? value : undefined;
};

/** What selling `packs` packs takes of each item — every item's quantity times the packs. */
export const packIssueLines = (items: Array<PackItem>, packs: number): Array<PackIssueLine> =>
  items.map((item) => ({
    name: item.variantLabel === null ? item.name : `${item.name} — ${item.variantLabel}`,
    count: item.quantity * packs,
  }));
